import { useCallback, useEffect, useState } from "react";
import { DownloadsScreen, type DownloadEntry } from "../../screens/practice/DownloadsScreen";
import { ScreenState } from "../../components/ScreenState";
import { TabBar } from "../../components/TabBar";
import { useCachedAsync } from "../../lib/useCachedAsync";
import { formatSize } from "../../lib/offlinePacks";
import { OfflineNote } from "../../components/OfflineNote";
import { fetchMe } from "../../api/me";
import { fetchPack } from "../../api/catalog";
import { offlineSupported, packs, pending } from "../../lib/offlineStore";
import { AUTO_UPDATE_KEY, autoUpdateEnabled, refreshPacksOnWifi } from "../../lib/packAutoUpdate";

/**
 * A subject pack is about 75 KB, not 75 MB.
 *
 * Whole megabytes turned every real pack into either "0 MB" or "1 MB" — a
 * number a student would catch the moment they opened their storage settings.
 */

/**
 * Real offline packs.
 *
 * The screen listed four invented subjects with invented sizes and a toggle
 * that did nothing. Downloading now actually fetches the subject's questions
 * and stores them in IndexedDB, and the size shown is what is really on the
 * phone.
 */
export function DownloadsRoute() {
  const me = useCachedAsync("me", fetchMe);
  const [stored, setStored] = useState<Record<string, { bytes: number }>>({});
  const [downloading, setDownloading] = useState<Record<string, number>>({});
  const [pendingCount, setPendingCount] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [autoUpdate, setAutoUpdate] = useState(autoUpdateEnabled);

  // The active profile's packs: its subjects are the ones listed.
  const exam = me.data?.activeExam ?? null;
  const refresh = useCallback(async () => {
    if (!offlineSupported()) return;
    const [all, count] = await Promise.all([packs.all(), pending.count()]);
    setStored(Object.fromEntries(all.map((pack) => [pack.subjectId, { bytes: pack.bytes }])));
    setPendingCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The toggle used to be stored and never read. On Wi-Fi with it on, stale
  // packs refresh here and at app start (main.tsx); on data, nothing moves.
  useEffect(() => {
    if (!autoUpdate) return;
    void refreshPacksOnWifi().then((count) => {
      if (count > 0) void refresh();
    });
  }, [autoUpdate, refresh]);

  const download = async (subjectId: string, subjectName: string) => {
    setDownloading((current) => ({ ...current, [subjectId]: 5 }));
    setFailure(null);

    try {
      const pack = await fetchPack(subjectId, exam);
      setDownloading((current) => ({ ...current, [subjectId]: 80 }));

      const serialised = JSON.stringify(pack.questions);
      await packs.save({
        subjectId,
        subjectName,
        exam,
        questions: pack.questions,
        downloadedAt: Date.now(),
        bytes: new Blob([serialised]).size,
      });

      await refresh();
    } catch (error) {
      // A download that dies silently leaves the row saying "Tap to download"
      // with no explanation, which reads as a button that does nothing.
      setFailure(
        error instanceof Error
          ? error.message
          : `Couldn't download ${subjectName}. Try again when you have signal.`,
      );
    } finally {
      setDownloading((current) => {
        const next = { ...current };
        delete next[subjectId];
        return next;
      });
    }
  };

  const entries: DownloadEntry[] = (me.data?.subjects ?? []).map((subject) => {
    const progress = downloading[subject.subjectId];
    const saved = stored[subject.subjectId];

    return {
      subject: subject.name,
      state: progress !== undefined ? "downloading" : saved ? "downloaded" : "not-downloaded",
      size: saved ? formatSize(saved.bytes) : null,
      ...(progress !== undefined ? { progress } : {}),
    };
  });

  const totalUsed = formatSize(Object.values(stored).reduce((sum, pack) => sum + pack.bytes, 0));

  return (
    <ScreenState loading={me.loading} error={me.error} onRetry={me.reload} footer={<TabBar />}>
      <OfflineNote since={me.staleSince} />
      {me.data ? (
        <DownloadsScreen
          entries={entries}
          totalUsed={totalUsed}
          autoUpdateOnWifi={autoUpdate}
          onToggleAutoUpdate={(enabled) => {
            setAutoUpdate(enabled);
            try {
              localStorage.setItem(AUTO_UPDATE_KEY, String(enabled));
            } catch {
              /* A preference that survives only this session is still better
                 than a toggle that does not move. */
            }
          }}
          pendingSyncCount={pendingCount}
          error={failure}
          onDownload={(subjectName) => {
            const subject = me.data?.subjects.find((entry) => entry.name === subjectName);
            if (subject) void download(subject.subjectId, subject.name);
          }}
        />
      ) : null}
    </ScreenState>
  );
}
